// brenthelsnow: hide Colour / Size / Length filter chips that only match sold-out variants.
//
// Shopify builds facet values from every variant, sold-out included, so a chip can lead to "N products"
// and an empty grid. With the Search & Discovery "Availability" filter enabled, rendering the page's facets
// with `filter.v.availability=1` makes Shopify combine colour/size/length with availability per variant,
// so a value that is absent in that response has no buyable variant. The answer comes from the tiny
// standalone section `sections/facet-availability-probe.liquid` (a few KB) rather than a re-render of the
// whole collection section; blocks/filters.liquid starts that request inline, before any module loads,
// and hands the promise over through `window.brenthelsnowFacetProbe`.
// Lists opt in with `data-availability-gate="pending"` (snippets/list-filter.liquid); only the marked
// lists are touched. Fails open: any error, or the Availability filter being off, leaves every chip visible.

const GATE_ATTR = 'data-availability-gate';
const UNBUYABLE_CLASS = 'facets__inputs-list-item--unbuyable';
const BREAK_CLASS = 'facets__inputs-list-break';
const EMPTY_BREAK_CLASS = 'facets__inputs-list-break--empty';
const AVAILABILITY_PARAM = 'filter.v.availability';
// Sale collections (only_sale_color_cards) also require a discount — list-filter marks their lists with
// data-sale-gate and the probe adds the S&D filter on the variant metafield custom.on_sale.
const SALE_GATE_ATTR = 'data-sale-gate';
const ON_SALE_PARAM = 'filter.v.m.custom.on_sale';
const PROBE_SECTION = 'facet-availability-probe';

/** @type {WeakMap<Element, number>} */
const runTokens = new WeakMap();
/** Lists that have been resolved at least once: on re-render they keep their verdict while refetching. */
const gatedOnce = new WeakSet();
/**
 * Probe responses by URL — shares in-flight requests between prefetch and resolve. Seeded from the
 * inline fetch that blocks/filters.liquid starts during HTML parsing.
 * @type {Map<string, Promise<string>>}
 */
const probes = new Map();
const FAIL_OPEN_MS = 3000;
let nextToken = 0;
let scheduled = false;
let lastPrefetchedUrl = '';

/**
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function keyOf(input) {
  return `${input.name}=${input.value}`;
}

/**
 * The probe URL for the current page — must build the same string as the inline script in
 * blocks/filters.liquid so the early request is picked up rather than repeated.
 * @returns {string}
 */
function probeUrl() {
  const url = new URL(window.location.href);
  url.searchParams.set(AVAILABILITY_PARAM, '1');
  if (document.querySelector(`[${SALE_GATE_ATTR}]`)) url.searchParams.set(ON_SALE_PARAM, '1');
  url.searchParams.set('section_id', PROBE_SECTION);
  url.searchParams.sort();
  return url.toString();
}

/**
 * Starts (or reuses) the probe request for a URL.
 * @param {string} url
 * @returns {Promise<string>}
 */
function probe(url) {
  let pending = probes.get(url);
  if (pending) return pending;
  const early = window.brenthelsnowFacetProbe;
  if (early && early.url === url && early.promise) {
    // Adopt the inline request once only: if it failed, a later retry for this URL must fetch again.
    window.brenthelsnowFacetProbe = null;
    pending = Promise.resolve(early.promise);
  } else {
    pending = fetch(url).then((response) => response.text());
  }
  // A failed request must not stick: the next re-render should ask again.
  pending = pending.catch((error) => {
    probes.delete(url);
    throw error;
  });
  probes.set(url, pending);
  return pending;
}

/**
 * Fetches the probe with the availability filter applied and returns the still-buyable values,
 * or null when that cannot be determined (fail open).
 * @param {string} url
 * @returns {Promise<Set<string> | null>}
 */
async function fetchBuyableValues(url) {
  try {
    const html = await probe(url);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // No probe wrapper means the response is not the probe (error page, rate limit) — unknown, not
    // "nothing is buyable".
    if (!doc.querySelector(`[${GATE_ATTR}]`)) {
      probes.delete(url);
      return null;
    }
    const buyable = new Set();
    for (const input of doc.querySelectorAll(`[${GATE_ATTR}] input[name]`)) {
      if (!(input instanceof HTMLInputElement) || input.disabled) continue;
      buyable.add(keyOf(input));
    }
    return buyable;
  } catch {
    return null;
  }
}

/**
 * Board Length pills are grouped by row breaks (list-filter.liquid); hide a break when the group before
 * it has no visible chip left, so gaps don't stack up.
 * @param {Element} list
 */
function collapseEmptyGroups(list) {
  let groupHasVisible = false;
  for (const item of list.children) {
    if (item.classList.contains(BREAK_CLASS)) {
      item.classList.toggle(EMPTY_BREAK_CLASS, !groupHasVisible);
      groupHasVisible = false;
    } else if (!item.classList.contains(UNBUYABLE_CLASS)) {
      groupHasVisible = true;
    }
  }
}

async function resolveGates() {
  scheduled = false;
  const lists = Array.from(document.querySelectorAll(`[${GATE_ATTR}="pending"]`));
  if (lists.length === 0) return;

  const token = ++nextToken;
  for (const list of lists) {
    runTokens.set(list, token);
    // After a re-render the items still carry the previous verdict (data-skip-node-update keeps them).
    // Keep it on screen while the new answer loads so the panel doesn't expand and collapse; the
    // timer below drops it if the fetch stalls, so the gate still fails open. First load stays hidden.
    if (gatedOnce.has(list)) list.setAttribute(GATE_ATTR, 'resolving');
  }
  const failOpen = setTimeout(() => {
    for (const list of lists) {
      if (runTokens.get(list) !== token) continue;
      for (const item of list.children) item.classList.remove(UNBUYABLE_CLASS);
      gatedOnce.add(list);
      list.setAttribute(GATE_ATTR, 'resolved');
    }
  }, FAIL_OPEN_MS);

  const buyable = await fetchBuyableValues(probeUrl());
  clearTimeout(failOpen);

  for (const list of lists) {
    if (!list.isConnected || runTokens.get(list) !== token) continue;
    gatedOnce.add(list);
    for (const item of list.children) {
      const input = item.querySelector('input[name]');
      let show = true;
      if (buyable && input instanceof HTMLInputElement) {
        show = input.checked || buyable.has(keyOf(input));
      }
      item.classList.toggle(UNBUYABLE_CLASS, !show);
    }
    collapseEmptyGroups(list);
    list.setAttribute(GATE_ATTR, 'resolved');
  }
}

function scheduleResolve() {
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(resolveGates);
}

// The facets section is morphed in place on every filter change; the server HTML brings the lists
// back as "pending", which is the signal to re-check against the new URL.
new MutationObserver(scheduleResolve).observe(document.body, {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: [GATE_ATTR],
});

// Facet clicks push the new URL synchronously and then render; start the probe for that URL right
// away so the answer is usually there by the time the morphed lists come back as "pending".
function prefetchForCurrentUrl() {
  setTimeout(() => {
    if (!document.querySelector(`[${GATE_ATTR}]`) || window.location.href === lastPrefetchedUrl) return;
    lastPrefetchedUrl = window.location.href;
    probe(probeUrl()).catch(() => {});
  }, 0);
}
document.addEventListener('change', prefetchForCurrentUrl);
document.addEventListener('click', prefetchForCurrentUrl);

scheduleResolve();
